/**
 * One submit is one DynamoDB transaction on the service side, and a transaction holds at most 100
 * items — so the picker stops the author before the request is refused rather than after.
 */
export const MAX_AUDIENCE_TARGETS_PER_SUBMIT = 100;

/** Audience-member shapes the tab needs — a structural subset of the Relay result. */
export interface DeployAudienceCompanySource {
	readonly companyId: string | null;
}

export interface DeployAudiencePersonSource {
	readonly personId: string | null;
	readonly companyId: string | null;
}

export interface DeploymentSource {
	readonly isLiveToAnyone: boolean | null;
	readonly companyCount: number | null;
	readonly userCount: number | null;
	readonly companies: ReadonlyArray<DeployAudienceCompanySource | null> | null;
	readonly persons: ReadonlyArray<DeployAudiencePersonSource | null> | null;
}

/** One table row: a company, whether it holds a wholesale grant, and which of its people are named individually. */
export interface NovaDeployCompanyRow {
	companyId: string;
	hasCompanyGrant: boolean;
	personIds: string[];
}

/** A named person, always carried with the company they belong to — the service records both on the entry. */
export interface AudiencePersonTarget {
	personId: string;
	companyId: string;
}

/** One side of an audience write — what `UpdateNovaAgentAudienceInput`'s own `add`/`remove` each carry. */
export interface AudienceTargets {
	companyIds: string[];
	persons: AudiencePersonTarget[];
}

/** How many targets one side of a write actually counts against the per-submit cap. */
export function audienceTargetsCount(targets: AudienceTargets): number {
	return targets.companyIds.length + targets.persons.length;
}

export interface NovaDeploymentView {
	isLiveToAnyone: boolean;
	companyCount: number;
	userCount: number;
	/**
	 * The audience grouped into one row per company, ordered by the order companies first appear in
	 * the audience: a company grant's own order among `companies`, followed by any company introduced
	 * only through a named person, in the order that person first appears in `persons`. A company
	 * holding both a wholesale grant and named individuals is a single row — the individuals are
	 * covered by the grant but still listed, because they remain independently removable.
	 */
	rows: NovaDeployCompanyRow[];
}

const isPresent = <T>(value: T | null | undefined): value is T => value != null;

function buildRows(companies: DeployAudienceCompanySource[], persons: DeployAudiencePersonSource[]): NovaDeployCompanyRow[] {
	// A Map preserves insertion order, which is exactly the ordering rule above: a company grant
	// claims its slot first, and a person only ever inserts a new row when their company has none yet.
	const rowsByCompanyId = new Map<string, NovaDeployCompanyRow>();
	for (const company of companies) {
		if (!company.companyId) {
			continue;
		}
		rowsByCompanyId.set(company.companyId, {companyId: company.companyId, hasCompanyGrant: true, personIds: []});
	}
	for (const person of persons) {
		if (!person.companyId || !person.personId) {
			continue;
		}
		const row = rowsByCompanyId.get(person.companyId);
		if (row) {
			row.personIds.push(person.personId);
		} else {
			rowsByCompanyId.set(person.companyId, {
				companyId: person.companyId,
				hasCompanyGrant: false,
				personIds: [person.personId],
			});
		}
	}
	return Array.from(rowsByCompanyId.values());
}

/**
 * Maps the Relay result into what the tab renders. `isLiveToAnyone` is taken from the server rather
 * than recomputed here: the server owns the rule, and two implementations of it would eventually
 * disagree.
 */
export function buildDeploymentView(source: DeploymentSource | null | undefined): NovaDeploymentView {
	const companyCount = source?.companyCount ?? 0;
	const userCount = source?.userCount ?? 0;
	const companies = (source?.companies ?? []).filter(isPresent);
	const persons = (source?.persons ?? []).filter(isPresent);
	return {
		isLiveToAnyone: source?.isLiveToAnyone ?? false,
		companyCount,
		userCount,
		rows: buildRows(companies, persons),
	};
}

/**
 * Whether the viewer's own company holds a wholesale grant in this deployment's audience — the
 * "Active for your company" toggle's state. `viewerCompanyId` must already be encoded the way an
 * audience entry's `companyId` is (see `encodeCompanyGlobalId` in `novaDeployDirectory.ts`): the
 * viewer's own company id from the schema is encoded differently, and comparing the two encodings
 * directly would never match.
 */
export function isViewerCompanyEnabled(view: NovaDeploymentView, viewerCompanyId: string | null): boolean {
	if (!viewerCompanyId) {
		return false;
	}
	return view.rows.some(row => row.companyId === viewerCompanyId && row.hasCompanyGrant);
}
